import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ChevronRightIcon} from '@/components/ui/icon/Icon'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import dayjs, {formatDate} from '@/dayjs'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {useGetNextAvailability} from '@/features/agenda/queries/useGetNextAvaiability'
import {useTranslation} from '@/translations/i18n'
import {useMemo} from 'react'
import {z} from 'zod'

const ConsultantNextAvailabilitySlot = z.object({
    id: z.string().min(1),
    fullName: z.string().min(1),
    specializationName: z.string().min(1),
    date: z.string().min(1)
})
type ConsultantNextAvailabilitySlot = z.infer<typeof ConsultantNextAvailabilitySlot>

export const EventNextAvailabilityButton = ({usersIds}: {usersIds: string[]}) => {
    const {t} = useTranslation()
    const {
        params: {date},
        navigateToDate
    } = useAgendaNavigation()

    const {
        data: nextAvailability,
        isLoading,
        isError
    } = useGetNextAvailability({
        therapists: usersIds,
        startDate: formatDate(dayjs(date).add(1, 'day').toDate()),
        endDate: formatDate(dayjs(date).add(1, 'month').toDate())
    })

    // Transform the data into a flattened array of consultant-date combinations
    const orderedNextAvailability: ConsultantNextAvailabilitySlot[] = useMemo(() => {
        if (!nextAvailability || nextAvailability.length === 0) return []

        // Create a map to track the current date index for each consultant
        const consultantDateIndices = new Map<string, number>()

        // Initialize all consultants with index 0
        nextAvailability.forEach(consultant => {
            consultantDateIndices.set(consultant.id, 0)
        })

        const result: ConsultantNextAvailabilitySlot[] = []
        const totalSlots = Math.max(...nextAvailability.map(c => c.dates.length))

        // For each slot position, find the consultant with the earliest date at that position
        for (let slotIndex = 0; slotIndex < totalSlots; slotIndex++) {
            let earliestDate = null
            let earliestConsultant = null

            // Find the consultant with the earliest date at the current position
            for (const consultant of nextAvailability) {
                const currentIndex = consultantDateIndices.get(consultant.id) || 0
                if (currentIndex < consultant.dates.length) {
                    const currentDate = consultant.dates[currentIndex]
                    if (!earliestDate || currentDate < earliestDate) {
                        earliestDate = currentDate
                        earliestConsultant = consultant
                    }
                }
            }

            // Add the earliest consultant-date combination to the result
            if (earliestConsultant && earliestDate) {
                const currentIndex = consultantDateIndices.get(earliestConsultant.id) || 0
                result.push({
                    id: earliestConsultant.id,
                    fullName: earliestConsultant.fullName,
                    specializationName: earliestConsultant.specializationName,
                    date: earliestDate
                })

                // Increment the index for this consultant
                consultantDateIndices.set(earliestConsultant.id, currentIndex + 1)
            }
        }

        return result
    }, [nextAvailability])

    const handleNextSlot = () => {
        const nextSubject = orderedNextAvailability[0 + 1]

        if (!nextSubject) return

        navigateToDate(dayjs(nextSubject.date).toDate())
    }

    return (
        <Flexbox direction="column" gap={1.5} fullWidth>
            <Text size="textSm" palette="neutral" shade="500">
                {t('agenda:next_available_slot_for_consultant')}
            </Text>
            <Flexbox justify="space-between" align="center" fullWidth>
                {isLoading ? (
                    <Skeleton width={100} height={20} />
                ) : isError ? (
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('errors:default')}
                    </Text>
                ) : nextAvailability ? (
                    <Button
                        variant="ghost"
                        shape="text"
                        size="sm"
                        fullWidth
                        onClick={handleNextSlot}
                        style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                    >
                        {orderedNextAvailability[0] ? (
                            <Flexbox fullWidth justify="space-between" align="center">
                                <Flexbox direction="column" gap={1}>
                                    <Text size="textSm">{orderedNextAvailability[0].fullName}</Text>
                                    <Text size="textSm" palette="neutral" shade="400">
                                        {formatDate(orderedNextAvailability[0].date, 'DD/MM/YYYY')}
                                    </Text>
                                </Flexbox>
                                <ChevronRightIcon />
                            </Flexbox>
                        ) : null}
                    </Button>
                ) : (
                    <Text size="textSm">{t('agenda:no_available_slots_next_6_months')}</Text>
                )}
            </Flexbox>
        </Flexbox>
    )
}
